'use client';

import React, {
    createContext,
    useContext,
    useState,
    useEffect,
    ReactNode,
    useCallback,
} from 'react';
import {
    useParams,
    useRouter,
    usePathname,
    // useSearchParams,
} from 'next/navigation';
import { Language } from '@cigarsnap/shared/types';
import { TranslationsType } from '@/types';
import { locales, defaultLocale, cookieName } from './settings';

type LanguageContextType = {
    language: Language;
    translations: TranslationsType; // This will now hold only the current language's translations
    isLoading: boolean;
    setLanguage: (lang: Language) => void;
};

const LanguageContext = createContext<LanguageContextType>({
    language: defaultLocale,
    translations: {}, // Initialize with empty object
    isLoading: true,
    setLanguage: () => {},
});

const isValidLocale = (
    locale: string | undefined | null
): locale is Language => {
    return typeof locale === 'string' && locales.includes(locale as Language);
};

export function LanguageProvider({ children }: { children: ReactNode }) {
    const [language, setLanguageState] = useState<Language>(defaultLocale);
    const [currentTranslations, setCurrentTranslations] =
        useState<TranslationsType>({});
    const [isLoading, setIsLoading] = useState(true);

    const params = useParams();
    const router = useRouter();
    const pathname = usePathname();
    // const searchParams = useSearchParams();

    const loadTranslations = useCallback(async (localeToLoad: Language) => {
        setIsLoading(true);
        try {
            const langModule = await import(`./locales/${localeToLoad}.json`);
            setCurrentTranslations(langModule.default);
            setLanguageState(localeToLoad); // Update language state after translations are loaded
            document.documentElement.lang = localeToLoad; // Set lang attribute
            document.documentElement.dir =
                localeToLoad === 'ar' ? 'rtl' : 'ltr'; // Set dir attribute
            console.log(
                `[LanguageProvider] Translations loaded for ${localeToLoad}`
            );
        } catch (error) {
            console.error(
                `[LanguageProvider] Failed to load translations for ${localeToLoad}:`,
                error
            );
            // Fallback to default locale if loading fails
            if (localeToLoad !== defaultLocale) {
                await loadTranslations(defaultLocale);
            } else {
                setCurrentTranslations({}); // Set empty if default also fails
            }
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        // Determine language from URL params on initial load or param change
        const urlLang = params.lang as string | undefined;
        const targetLang = isValidLocale(urlLang) ? urlLang : defaultLocale;

        if (
            targetLang !== language ||
            Object.keys(currentTranslations).length === 0
        ) {
            // Load if target language is different or translations are not yet loaded
            loadTranslations(targetLang);
        } else {
            // If language is already correct and translations loaded, ensure loading is false
            setIsLoading(false);
            document.documentElement.lang = targetLang; // Ensure lang attribute is set
            document.documentElement.dir = targetLang === 'ar' ? 'rtl' : 'ltr'; // Ensure dir attribute is set
        }
    }, [params.lang, loadTranslations, language, currentTranslations]); // Rerun when URL language param changes, also include language and currentTranslations as per exhaustive-deps common practice

    const handleLanguageChange = (newLang: Language) => {
        if (newLang === language && Object.keys(currentTranslations).length > 0)
            return;

        const currentPath = pathname || '/';
        const segments = currentPath.split('/');
        segments[1] = newLang; // Update the language segment

        // const search = searchParams?.toString();
        const search = ''; // Temporarily remove searchParams usage for testing CSR
        const newPath = segments.join('/') + (search ? `?${search}` : '');

        document.cookie = `${cookieName}=${newLang}; path=/; max-age=31536000; SameSite=Lax`;

        // We are pushing to a new path. The useEffect above will detect the change
        // in `params.lang` and call `loadTranslations(newLang)`.
        router.push(newPath);
    };

    return (
        <LanguageContext.Provider
            value={{
                language,
                translations: currentTranslations, // Provide only current translations
                isLoading,
                setLanguage: handleLanguageChange,
            }}
        >
            {children}
        </LanguageContext.Provider>
    );
}

export const useLanguage = () => useContext(LanguageContext);
